<main id="main-content" class="content-page">
  <nav class="breadcrumbs" aria-label="Breadcrumb">
    <a routerLink="/">{{ ct('home') }}</a
    ><span aria-hidden="true">/</span>
    @if (!isIndex()) {
      <a [href]="contentBasePath()">{{ ct('guides') }}</a
      ><span aria-hidden="true">/</span>
      <span aria-current="page">{{ page()?.title }}</span>
    } @else {
      <span aria-current="page">{{ ct('guides') }}</span>
    }
  </nav>

  @if (page(); as article) {
    <article class="article-shell">
      <header class="article-intro">
        <p class="eyebrow">{{ article.category ?? ct('knowledgeCenter') }}</p>
        <h1>{{ article.title }}</h1>
        <p class="lead">{{ article.summary }}</p>
        <p class="reviewed">{{ ct('reviewed') }} {{ lastReviewed }} · {{ ct('evidence') }}</p>
        <nav class="share-actions" aria-label="Share this IQ guide">
          <span>Share this guide</span>
          <a [href]="shareUrl('linkedin')" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          <a [href]="shareUrl('facebook')" target="_blank" rel="noopener noreferrer">Facebook</a>
          <a [href]="shareUrl('x')" target="_blank" rel="noopener noreferrer">X</a>
          <a [href]="shareUrl('email')">Email</a>
          <button type="button" (click)="copyArticleLink()">Copy link</button>
        </nav>
      </header>

      @if (article.tool) {
        <section dsCard class="tool-panel" [attr.aria-labelledby]="'tool-' + article.slug">
          <p class="eyebrow">INTERACTIVE TOOL</p>
          <h2 [id]="'tool-' + article.slug">{{ article.title }}</h2>

          @if (article.tool === 'percentile') {
            <label for="iq-score-tool">IQ score</label>
            <div class="tool-input-row">
              <input
                dsInput
                id="iq-score-tool"
                type="number"
                min="40"
                max="200"
                [value]="firstValue()"
                (input)="setFirst($any($event.target).value)"
              />
              <button dsButton type="button" (click)="calculate(article.tool)">
                Calculate percentile
              </button>
            </div>
          } @else if (article.tool === 'mental-age') {
            <div class="tool-fields">
              <label
                >Historical IQ value<input
                  dsInput
                  type="number"
                  min="1"
                  max="200"
                  [value]="firstValue()"
                  (input)="setFirst($any($event.target).value)"
              /></label>
              <label
                >Chronological age<input
                  dsInput
                  type="number"
                  min="1"
                  max="100"
                  [value]="secondValue()"
                  (input)="setSecond($any($event.target).value)"
              /></label>
            </div>
            <button dsButton type="button" (click)="calculate(article.tool)">
              Show historical formula
            </button>
          } @else if (article.tool === 'reaction-time') {
            <button
              dsButton
              type="button"
              class="reaction-target"
              [class.ready]="reactionState() === 'ready'"
              (click)="recordReaction()"
            >
              {{
                reactionState() === 'ready'
                  ? 'PRESS NOW'
                  : reactionState() === 'waiting'
                    ? 'WAIT…'
                    : 'START TRIAL'
              }}
            </button>
          } @else if (article.tool === 'memory') {
            <label
              >Sequence length
              <input
                dsInput
                type="number"
                min="4"
                max="12"
                [value]="firstValue()"
                (input)="setFirst($any($event.target).value)"
              />
            </label>
            <button dsButton type="button" (click)="startMemory()">Show new sequence</button>
            @if (memoryVisible()) {
              <div class="memory-sequence" aria-live="polite">{{ memorySequence() }}</div>
            } @else if (memorySequence()) {
              <div class="tool-input-row">
                <input
                  dsInput
                  inputmode="numeric"
                  autocomplete="off"
                  aria-label="Remembered sequence"
                  [value]="memoryAnswer()"
                  (input)="setMemoryAnswer($any($event.target).value)"
                />
                <button dsButton type="button" (click)="checkMemory()">Check recall</button>
              </div>
            }
          } @else {
            <p class="tool-prompt">
              @switch (article.tool) {
                @case ('brain-age') {
                  Which description best matches your approach to a new puzzle?
                }
                @case ('color-vision') {
                  Which contrast level is easiest for you to distinguish on this display?
                }
                @case ('cognitive-style') {
                  How do you usually begin an unfamiliar problem?
                }
                @case ('learning-style') {
                  Which study activity do you most often choose first?
                }
              }
            </p>
            <div class="tool-choices">
              <button dsButton type="button" (click)="chooseToolAnswer(article.tool, 0)">
                @switch (article.tool) {
                  @case ('brain-age') {
                    Slowly and carefully
                  }
                  @case ('color-vision') {
                    Low contrast
                  }
                  @case ('cognitive-style') {
                    Make a step-by-step plan
                  }
                  @case ('learning-style') {
                    Read and outline
                  }
                }
              </button>
              <button dsButton type="button" (click)="chooseToolAnswer(article.tool, 1)">
                @switch (article.tool) {
                  @case ('brain-age') {
                    Balance speed and accuracy
                  }
                  @case ('color-vision') {
                    Medium contrast
                  }
                  @case ('cognitive-style') {
                    Mix planning and exploration
                  }
                  @case ('learning-style') {
                    Discuss and compare examples
                  }
                }
              </button>
              <button dsButton type="button" (click)="chooseToolAnswer(article.tool, 2)">
                @switch (article.tool) {
                  @case ('brain-age') {
                    Try quickly and adapt
                  }
                  @case ('color-vision') {
                    High contrast
                  }
                  @case ('cognitive-style') {
                    Explore the whole pattern first
                  }
                  @case ('learning-style') {
                    Practice by doing
                  }
                }
              </button>
            </div>
          }

          @if (toolResult()) {
            <p class="tool-result" role="status">{{ toolResult() }}</p>
          }
          <p class="tool-disclaimer">
            Educational activity only — not standardized, diagnostic, or suitable for high-stakes
            decisions.
          </p>
        </section>
      }

      <div class="article-layout">
        <aside class="toc" [attr.aria-label]="ct('onThisPage')">
          <strong>{{ ct('onThisPage') }}</strong>
          @for (section of article.sections; track section.heading; let index = $index) {
            <a [href]="pagePath() + '#section-' + index" [attr.title]="section.heading">{{
              tocLabel(section.heading)
            }}</a>
          }
          <a [href]="pagePath() + '#frequently-asked-questions'">{{ ct('faq') }}</a>
          @if (article.sources?.length) {
            <a [href]="pagePath() + '#sources'">{{ ct('references') }}</a>
          }
        </aside>

        <div class="article-body">
          @for (section of article.sections; track section.heading; let index = $index) {
            <section [id]="'section-' + index">
              <h2>{{ section.heading }}</h2>
              @for (paragraph of section.paragraphs; track paragraph) {
                <p>{{ paragraph }}</p>
              }
              @if (section.bullets) {
                <ul>
                  @for (item of section.bullets; track item) {
                    <li>{{ item }}</li>
                  }
                </ul>
              }
            </section>
          }

          <section id="frequently-asked-questions" class="faq">
            <p class="eyebrow">{{ ct('commonQuestions') }}</p>
            <h2>{{ ct('faq') }}</h2>
            @for (faq of article.faqs; track faq.question) {
              <details>
                <summary>{{ faq.question }}</summary>
                <p>{{ faq.answer }}</p>
              </details>
            }
          </section>

          @if (article.sources?.length) {
            <section id="sources" class="sources">
              <p class="eyebrow">{{ ct('references') }}</p>
              <h2>{{ ct('sourcesTitle') }}</h2>
              <ul>
                @for (source of article.sources; track source.url) {
                  <li>
                    <a [href]="source.url" target="_blank" rel="noopener">{{ source.label }}</a>
                  </li>
                }
              </ul>
            </section>
          }

          <aside class="editorial-note">
            <strong>{{ ct('importantContext') }}</strong>
            <p>{{ ct('disclaimer') }}</p>
          </aside>
        </div>
      </div>
    </article>

    <section class="related" aria-labelledby="related-heading">
      <p class="eyebrow">{{ ct('continueLearning') }}</p>
      <h2 id="related-heading">{{ ct('relatedGuides') }}</h2>
      <div class="card-grid">
        @for (related of relatedPages(article); track related.slug) {
          <article dsCard class="content-card">
            <h3>{{ related.title }}</h3>
            <p>{{ related.description }}</p>
            <a [href]="urlFor(related.slug)">{{ cardLinkLabel(related) }}</a>
          </article>
        }
      </div>
    </section>
  } @else {
    <section class="index-intro">
      <p class="eyebrow">{{ ct('knowledgeCenter') }}</p>
      <h1>{{ ct('indexTitle') }}</h1>
      <p>{{ ct('indexLead') }}</p>
    </section>
    <section class="discovery" aria-label="Featured IQ guides">
      <section aria-labelledby="popular-guides-title">
        <div class="discovery-heading">
          <p class="eyebrow">Start here</p>
          <h2 id="popular-guides-title">Popular guides</h2>
        </div>
        <div class="discovery-grid">
          @for (article of popularContentPages(); track article.slug) {
            <a [href]="urlFor(article.slug)"
              ><span>{{ article.category }}</span
              ><strong>{{ article.title }}</strong></a
            >
          }
        </div>
      </section>
      <section aria-labelledby="recommended-guides-title">
        <div class="discovery-heading">
          <p class="eyebrow">Explore broadly</p>
          <h2 id="recommended-guides-title">Recommended by topic</h2>
        </div>
        <div class="discovery-grid">
          @for (article of recommendedContentPages(); track article.slug) {
            <a [href]="urlFor(article.slug)"
              ><span>{{ article.category }}</span
              ><strong>{{ article.title }}</strong></a
            >
          }
        </div>
      </section>
      <section aria-labelledby="recent-guides-title">
        <div class="discovery-heading">
          <p class="eyebrow">New in the library</p>
          <h2 id="recent-guides-title">Recently added</h2>
        </div>
        <div class="discovery-grid">
          @for (article of recentContentPages(); track article.slug) {
            <a [href]="urlFor(article.slug)"
              ><span>{{ article.category }}</span
              ><strong>{{ article.title }}</strong></a
            >
          }
        </div>
      </section>
    </section>
    <section class="content-directory" aria-labelledby="directory-heading">
      <div class="directory-toolbar">
        <div>
          <p class="eyebrow">{{ ct('explore') }}</p>
          <h2 id="directory-heading">{{ ct('browseGuides') }}</h2>
        </div>
        <form class="content-search" role="search" (submit)="$event.preventDefault()">
          <label for="content-search">{{ ct('searchLabel') }}</label>
          <div class="content-search-field">
            <ds-icon name="search" />
            <input
              dsInput
              id="content-search"
              type="search"
              autocomplete="off"
              [placeholder]="ct('searchPlaceholder')"
              [value]="pageQuery()"
              (input)="setContentQuery($any($event.target).value)"
            />
          </div>
        </form>
      </div>

      <div class="category-filter" aria-labelledby="category-filter-label">
        <span id="category-filter-label">{{ ct('filterByTopic') }}</span>
        <div class="category-options" role="group" aria-labelledby="category-filter-label">
          <button
            type="button"
            [class.is-active]="selectedCategory() === 'all'"
            [attr.aria-pressed]="selectedCategory() === 'all'"
            (click)="selectContentCategory('all')"
          >
            {{ ct('allTopics') }} <span>{{ pages().length }}</span>
          </button>
          @for (choice of categoryChoices(); track choice.category) {
            <button
              type="button"
              [class.is-active]="selectedCategory() === choice.category"
              [attr.aria-pressed]="selectedCategory() === choice.category"
              (click)="selectContentCategory(choice.category)"
            >
              {{ choice.category }} <span>{{ choice.count }}</span>
            </button>
          }
        </div>
      </div>

      <div class="directory-results-heading">
        <p aria-live="polite" aria-atomic="true">
          <strong>{{ filteredContentPages().length }}</strong> {{ ct('results') }}
        </p>
        @if (hasActiveContentFilters()) {
          <button
            dsButton
            type="button"
            variant="quiet"
            size="small"
            (click)="clearContentFilters()"
          >
            {{ ct('clearFilters') }}
          </button>
        }
      </div>

      @if (visibleContentPages().length) {
        <div class="index-grid">
          @for (article of visibleContentPages(); track article.slug) {
            <article dsCard class="content-card">
              <p class="card-category">{{ article.category ?? 'IQ essentials' }}</p>
              <h3>{{ article.title }}</h3>
              <p>{{ article.description }}</p>
              <a [href]="urlFor(article.slug)">{{ cardLinkLabel(article) }}</a>
            </article>
          }
        </div>
        @if (hasMoreContent()) {
          <div class="load-more">
            <button dsButton type="button" variant="secondary" (click)="showMoreContent()">
              {{ ct('showMore') }}
            </button>
          </div>
        }
      } @else {
        <div class="empty-results" role="status">
          <ds-icon name="search" />
          <h3>{{ ct('noResults') }}</h3>
          <p>{{ ct('noResultsText') }}</p>
          <button dsButton type="button" variant="secondary" (click)="clearContentFilters()">
            {{ ct('clearFilters') }}
          </button>
        </div>
      }
    </section>
  }

  <section class="cta">
    <div>
      <p class="eyebrow">{{ ct('ready') }}</p>
      <h2>{{ ct('ctaTitle') }}</h2>
      <p>{{ ct('ctaText') }}</p>
    </div>
    <a dsButton size="large" routerLink="/">{{ ct('startTest') }}</a>
  </section>
</main>
